/**
 * Loads a Google carousel AD's cards and their images in two requests, the same
 * way useConceptCreativeTree loads an ADSP concept: first the CAROUSEL_CARD rows
 * under the AD, then every image ASSET of those cards in one batched query.
 * Carousel images hang off their card row, not the AD, so the AD-parented asset
 * strip used for image ads would find none.
 *
 * Images are fetched by card row `_id` (parentEntityIds) because card source ids
 * embed the AD's source id and a GTI; the response still joins images to cards by
 * parentSourceEntityId. Nothing is status-filtered: retired cards stay visible
 * with their own badge, after the live ones.
 */
import { useMemo } from 'react';
import type { PublisherEntityDto } from 'src/store/generated/leela-api-generated';
import { narrowCarouselCardMetadata } from 'src/utils/publisherEntityMetadata';
import { compareByStatus } from 'src/utils/publisherEntityStatus';
import { groupByParentSourceEntityId } from './adspEntityHelpers';
import { ADSP_MAX_PAGE_SIZE, useAdspEntityLevel } from './useAdspEntityLevel';

interface CarouselCardTreeArgs {
  /** The carousel AD row's `sourceEntityId`; the tree stays unloaded without it. */
  adSourceEntityId?: string;
  campaignId: string;
}

export interface CarouselCardTree {
  cards: PublisherEntityDto[];
  imagesByCard: Map<string, PublisherEntityDto[]>;
  isError: boolean;
  isLoading: boolean;
}

const NO_ENTITIES: PublisherEntityDto[] = [];

export function useCarouselCardTree ({
  adSourceEntityId,
  campaignId,
}: CarouselCardTreeArgs): CarouselCardTree {
  const hasAdSourceEntityId = adSourceEntityId != null && adSourceEntityId.length > 0;
  const cardLevel = useAdspEntityLevel({
    campaignId,
    pageSize: ADSP_MAX_PAGE_SIZE,
    parentSourceEntityId: adSourceEntityId,
    publisher: 'google',
    publisherEntityType: 'CAROUSEL_CARD',
    skip: !hasAdSourceEntityId,
  });
  const cardRows = (cardLevel.query.data?.data ?? NO_ENTITIES) as PublisherEntityDto[];
  const cards = useMemo(() => [...cardRows].sort(compareCarouselCards), [cardRows]);
  const cardIds = useMemo(() => cards.map(card => card._id), [cards]);

  const imageLevel = useAdspEntityLevel({
    campaignId,
    pageSize: ADSP_MAX_PAGE_SIZE,
    parentEntityIds: cardIds,
    publisher: 'google',
    publisherEntityType: 'ASSET',
    skip: cardIds.length === 0,
  });
  const images = (imageLevel.query.currentData?.data ?? NO_ENTITIES) as PublisherEntityDto[];
  const imagesByCard = useMemo(() => groupByParentSourceEntityId(images), [images]);

  const isAwaitingImages = cardIds.length > 0 && !imageLevel.query.isError &&
    imageLevel.query.currentData === undefined;

  return {
    cards,
    imagesByCard,
    isError: cardLevel.query.isError || imageLevel.query.isError,
    isLoading: cardLevel.query.isLoading || isAwaitingImages,
  };
}

/** Live cards first, then by carousel position; cards without a position go last. */
function compareCarouselCards (a: PublisherEntityDto, b: PublisherEntityDto): number {
  return compareByStatus(a, b) || readCardIndex(a) - readCardIndex(b);
}

function readCardIndex (card: PublisherEntityDto): number {
  return narrowCarouselCardMetadata(card.publisherMetadata)?.cardIndex ?? Number.MAX_SAFE_INTEGER;
}
